import { AlertCircleIcon } from "lucide-react-native";
import React, { memo } from "react";
import {
  Control,
  Controller,
  ControllerRenderProps,
  FieldErrors,
} from "react-hook-form";
import { Platform } from "react-native";

import CustomSelect from "@/components/molecules/CustomSelect";
import { Button } from "@/components/atoms";
import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
  FormControlLabel,
  FormControlLabelText,
} from "@/components/atoms/FormControl";
import { Input } from "@/components/atoms/Input";
import { KeyboardAvoidingView } from "@/components/atoms/KeyboardAvoidingView";
import { ScrollView } from "@/components/atoms/ScrollView";
import { Stack } from "@/components/atoms/Stack";
import { CurrencyCode, InvitationsSportFragment } from "@/generated/graphql";
/**
 * Props for the OrganizationFormView component.
 */
interface Props {
  isValid: boolean;
  loading: boolean;
  onSubmit: () => void;
  control: Control<IForm>;
  errors: FieldErrors<IForm>;
  sports: InvitationsSportFragment[];
  isSmallScreen: boolean;
  keyboardVerticalOffset?: number;
}

/**
 * Form data interface for creating or updating an organization.
 */
export interface IForm {
  name: string;
  currencyCode: CurrencyCode;
  sportId: string;
  intakeFormUrl?: string | null;
}

const currencyCodes = Object.entries(CurrencyCode);

/**
 * Factory function for rendering name input field
 * @param loading - Whether the form is in a loading state
 * @param errors - Form validation errors object
 */
const renderNameField = (loading: boolean, errors: FieldErrors<IForm>) =>
  function NameField({
    field: { onChange, onBlur, value },
  }: {
    field: ControllerRenderProps<IForm, "name">;
  }) {
    return (
      <FormControl size="md" isInvalid={!loading && !!errors.name}>
        <FormControlLabel className="pb-1">
          <FormControlLabelText>Name</FormControlLabelText>
        </FormControlLabel>
        <Input
          variant="outline"
          className="border-2"
          size="xl"
          field={{
            placeholder: "Enter Name",
            type: "text",
            value: value ?? "",
            keyboardType: "default",
            inputMode: "text",
            autoCorrect: false,
            autoComplete: "organization",
            textContentType: "organizationName",
            autoCapitalize: "words",
            returnKeyLabel: "Next",
            enterKeyHint: "next",
            onChangeText: onChange,
            onBlur,
            className: "font-body",
          }}
        />

        <FormControlError>
          <FormControlErrorIcon as={AlertCircleIcon} />
          <FormControlErrorText className="shrink">
            {errors.name?.message}
          </FormControlErrorText>
        </FormControlError>
      </FormControl>
    );
  };

/**
 * Factory function for rendering sport selection field
 * @param loading - Whether the form is in a loading state
 * @param errors - Form validation errors object
 * @param sports - Array of available sport options
 */
const renderSportIdField = (
  loading: boolean,
  errors: FieldErrors<IForm>,
  sports: InvitationsSportFragment[]
) =>
  function SportIdField({
    field: { onChange, value },
  }: {
    field: ControllerRenderProps<IForm, "sportId">;
  }) {
    return (
      <FormControl size="md" isInvalid={!loading && !!errors.sportId}>
        <FormControlLabel className="pb-1">
          <FormControlLabelText>Sport</FormControlLabelText>
        </FormControlLabel>
        <CustomSelect
          options={sports?.map(item => ({
            label: item.name,
            value: item.id,
          }))}
          defaultValue={value}
          placeholder="Select Sport"
          onChangeValue={onChange}
        />
        <FormControlError>
          <FormControlErrorIcon as={AlertCircleIcon} />
          <FormControlErrorText className="shrink">
            {errors.sportId?.message}
          </FormControlErrorText>
        </FormControlError>
      </FormControl>
    );
  };

/**
 * Factory function for rendering currency code selection field
 * @param loading - Whether the form is in a loading state
 * @param errors - Form validation errors object
 */
const renderCurrencyCodeField = (
  loading: boolean,
  errors: FieldErrors<IForm>
) =>
  function CurrencyCodeField({
    field: { onChange, value },
  }: {
    field: ControllerRenderProps<IForm, "currencyCode">;
  }) {
    return (
      <FormControl size="md" isInvalid={!loading && !!errors.currencyCode}>
        <FormControlLabel className="pb-1">
          <FormControlLabelText>Currency Code</FormControlLabelText>
        </FormControlLabel>
        <CustomSelect
          options={currencyCodes.map(([_, name]) => ({
            value: name,
            label: name,
          }))}
          defaultValue={value}
          placeholder="Select Currency"
          onChangeValue={onChange}
        />
        <FormControlError>
          <FormControlErrorIcon as={AlertCircleIcon} />
          <FormControlErrorText className="shrink">
            {errors.currencyCode?.message}
          </FormControlErrorText>
        </FormControlError>
      </FormControl>
    );
  };

/**
 * Factory function for rendering intake form URL field
 * @param loading - Whether the form is in a loading state
 * @param errors - Form validation errors object
 * @param onSubmit - Callback function for form submission
 */
const renderIntakeFormUrlField = (
  loading: boolean,
  errors: FieldErrors<IForm>,
  onSubmit: () => void
) =>
  function IntakeFormUrlField({
    field: { onChange, onBlur, value },
  }: {
    field: ControllerRenderProps<IForm, "intakeFormUrl">;
  }) {
    return (
      <FormControl
        size="md"
        isDisabled={false}
        isInvalid={!loading && !!errors.intakeFormUrl}
        isReadOnly={false}
        isRequired={false}
      >
        <FormControlLabel className="pb-1">
          <FormControlLabelText>Intake Form URL</FormControlLabelText>
        </FormControlLabel>
        <Input
          variant="outline"
          className="border-2"
          size="xl"
          field={{
            placeholder: "Enter URL",
            type: "text",
            value: value ?? "",
            keyboardType: "url",
            inputMode: "url",
            autoCorrect: false,
            autoComplete: "off",
            textContentType: "none",
            autoCapitalize: "none",
            returnKeyLabel: "Done",
            enterKeyHint: "done",
            onChangeText: onChange,
            onBlur,
            onSubmitEditing: onSubmit,
            className: "font-body",
          }}
        />

        <FormControlError>
          <FormControlErrorIcon as={AlertCircleIcon} />
          <FormControlErrorText className="shrink">
            {errors.intakeFormUrl?.message}
          </FormControlErrorText>
        </FormControlError>
      </FormControl>
    );
  };

/**
 * View component for displaying organization creation and editing form with input fields and validation.
 * @param root0 - The component props
 * @param root0.isValid - Boolean indicating if all form fields are valid
 * @param root0.onSubmit - Callback function to handle form submission
 * @param root0.control - React Hook Form control object for managing form state
 * @param root0.errors - Object containing validation errors for each form field
 * @param root0.loading - Boolean indicating if form submission is in progress
 * @param root0.sports - Array of available sport options for the sport dropdown selector
 * @param root0.isSmallScreen - Boolean indicating if the display is a small mobile screen
 * @param root0.keyboardVerticalOffset - Optional vertical offset for keyboard avoidance behavior
 */
const OrganizationFormView = ({
  isValid,
  onSubmit,
  control,
  errors,
  loading,
  sports,
  isSmallScreen,
  keyboardVerticalOffset,
}: Props) => (
  <KeyboardAvoidingView
    behavior={Platform.OS === "ios" ? "padding" : "height"}
    // eslint-disable-next-line no-restricted-syntax -- KeyboardAvoidingView is a bare react-native re-export (no cssInterop for className, no UNSAFE_style forwarding); the desktop 640px centered form width is off the closed sizing ladder (max token 384px)
    style={!isSmallScreen ? { flex: 1, width: 640, alignSelf: "center" } : {}}
    keyboardVerticalOffset={keyboardVerticalOffset}
  >
    <ScrollView
      keyboardShouldPersistTaps="handled"
      className={!isSmallScreen ? "flex-1" : undefined}
      contentContainerClassName="grow py-6"
    >
      <Stack className="flex-1 gap-6 px-4 pt-6">
        <Controller
          name="name"
          control={control}
          render={renderNameField(loading, errors)}
        />
        <Controller
          name="sportId"
          control={control}
          render={renderSportIdField(loading, errors, sports)}
        />
        <Controller
          name="currencyCode"
          control={control}
          render={renderCurrencyCodeField(loading, errors)}
        />
        <Controller
          name="intakeFormUrl"
          control={control}
          render={renderIntakeFormUrlField(loading, errors, onSubmit)}
        />
      </Stack>
    </ScrollView>
    <Stack className="px-4 pb-4">
      <Button
        label={!loading ? "Submit" : "Please Wait..."}
        isDisabled={loading || !isValid}
        onPress={onSubmit}
        className="bg-surface-action"
        size="lg"
        labelTone="info"
        labelVariant="body"
      />
    </Stack>
  </KeyboardAvoidingView>
);

OrganizationFormView.displayName = "OrganizationFormView";

export default memo(OrganizationFormView);
